import risk_comparison from '../../../../images/productFocus/risk_comparison.png';
import raptor_platforms from '../../../../images/productFocus/raptor_platforms.png';
import raptor_reports from '../../../../images/productFocus/raptor_reports.png';
import supplementary_rules from '../../../../images/productFocus/supplementary_rules.png';
import RSCard from '../../../ui/containers/RSCard';
import { useState } from 'react';
import GenericModal from '../../../ui/GenericModal';

// Product Focus Data - Update this monthly with new features
const PRODUCT_FOCUS_FEATURES = [
  {
    id: 1,
    title: 'Risk Comparison Timeseries',
    image: risk_comparison,
    imageAlt: 'Risk Comparison Chart',
    headline: 'Unlock Deeper Insights',
    description:
      'You can now view a historical timeseries on the risk comparison page, offering a clearer picture of position changes over time via a dropdown on each asset row.',
    monthYear: 'June 2025',
    details: [
      'The risk comparison page helps track changes in positions over time. However, users wanted to see not just if a position changed, but exactly when and by how much.',
      'Now, each row features a dropdown showing the full position timeseries, pinpointing change dates and levels.',
      'This streamlines the user experience, enabling faster, more informed decisions by surfacing the right historical data directly in context.',
      'This feature is now live for all users. We hope you find it helpful!',
    ],
  },
  {
    id: 2,
    title: 'Raptor Platforms',
    image: raptor_platforms,
    imageAlt: 'Risk Comparison Chart',
    headline: 'Customise Your View',
    description:
      'Effortlessly organise your funds with customisable platforms, enabling you to view and compare aggregate data across tailored groups of funds.',
    monthYear: 'October 2025',
    details: [
      'Previously, all authorised funds for a client were displayed together on aggregate pages, making it challenging to analyse and compare data across logical groupings.',
      'With platforms, you can now create and manage custom groupings, allowing you to easily view and compare aggregate data specific to each platform.',
      'This feature is optional and can be enabled by contacting a member of the RiskSystem team.',
    ],
  },
  {
    id: 3,
    title: 'Updated PDF reports',
    image: raptor_reports,
    imageAlt: 'PDF reporting preview',
    headline: 'Coming soon',
    description:
      'Updated PDFs reporting for Liquidity and Risk Analysis, using modern charts and graphics.',
    monthYear: 'April 2026',
    details: [
      'We are replacing the previous Liquidity and Risk Analysis PDF reports with a cleaner, more readable design. Reports remain per fund so you get outputs tailored to each portfolio.',
      'This updated report design aligns closely with the Raptor platform.',
      'Reach out for a demo of the new reports. Migration to the new reports will begin in the coming months as we complete testing and sign-off.',

    ],
  },
  {
    id: 4,
    title: 'Supplementary Rules',
    image: supplementary_rules,
    imageAlt: 'Supplementary Rules',
    headline: 'Monitor internal limits alongside prospectus restrictions',
    description:
      'Track internal guidelines, house limits, or bespoke mandates that are not required by a fund’s prospectus, using the same alert system as prospectus restrictions.',
    monthYear: 'July 2026',
    details: [
      'The Supplementary Rules feature extends our Prospectus Restriction framework, enabling systematic monitoring of internal guidelines, house limits, or bespoke mandates that you wish to track but that are not explicitly required by a fund’s prospectus.',
      'All active internal and supplementary restrictions display directly on the fund overview page alongside standard prospectus constraints, giving you a centralised compliance view with the familiar alert system.',
      'To establish or adjust these internal limits, send the specific rule criteria to RiskSystem. Our team will handle the configuration and implementation of each rule promptly.',
    ],
  },
  // Add more features here as they become available
];

const ProductFocus = () => {
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(() =>
    Math.max(PRODUCT_FOCUS_FEATURES.length - 1, 0),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentFeature = PRODUCT_FOCUS_FEATURES[currentFeatureIndex];

  const mostRecentIndex = Math.max(PRODUCT_FOCUS_FEATURES.length - 1, 0);
  const isMostRecent = currentFeatureIndex === mostRecentIndex;
  const isOldest = currentFeatureIndex === 0;

  const nextFeature = () => {
    if (!isMostRecent && PRODUCT_FOCUS_FEATURES.length > 1) {
      setCurrentFeatureIndex(currentFeatureIndex + 1);
    }
  };

  const prevFeature = () => {
    if (!isOldest && PRODUCT_FOCUS_FEATURES.length > 1) {
      setCurrentFeatureIndex(currentFeatureIndex - 1);
    }
  };

  return (
    <RSCard key="product-section" className="flex h-full w-full flex-col overflow-hidden">
      <div className="border-b border-gray-200 pb-2">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-mainblue">
            Product Focus
          </h1>
        </div>
      </div>
      <div className="mb-4 mt-4 flex min-h-0 flex-1 flex-col">
        {/* Header with badge and navigation */}
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2"></div>
          <div className="flex items-center gap-1">
            <button
              onClick={prevFeature}
              disabled={isOldest}
              className={`rounded-md p-1 transition-colors ${isOldest
                ? 'cursor-not-allowed text-gray-300'
                : 'cursor-pointer text-gray-600 hover:bg-gray-100'
                }`}
              aria-label="Previous feature"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="inline-flex items-center rounded-full bg-blue-50 px-4 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              {currentFeature.monthYear}
            </span>
            <button
              onClick={nextFeature}
              disabled={isMostRecent}
              className={`rounded-md p-1 transition-colors ${isMostRecent
                ? 'cursor-not-allowed text-gray-300'
                : 'cursor-pointer text-gray-600 hover:bg-gray-100'
                }`}
              aria-label="Next feature"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content area */}
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="group flex min-h-0 flex-1 flex-col space-y-4 rounded-md border-2 border-transparent bg-white p-4 text-left shadow-sm transition-all duration-300 hover:border-mainblue/30 hover:bg-mainblue/5 hover:shadow-lg hover:shadow-mainblue/10 focus:border-mainblue/50 focus:bg-mainblue/5 focus:shadow-lg focus:shadow-mainblue/10 focus:outline-none"
          aria-label={`Open details for ${currentFeature.title}`}
        >
          <div className="relative flex min-h-[2rem] items-start justify-between">
            <h3 className="px-2 text-lg font-semibold text-gray-900 transition-colors group-hover:text-mainblue">
              {currentFeature.title}
            </h3>
            <div className="inline-flex items-center gap-2 rounded-full bg-transparent px-3 py-1 text-xs font-semibold text-mainblue transition-colors duration-200 group-hover:bg-mainblue/10">
              Click to learn more
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </div>
          </div>

          <div className="mx-2 flex aspect-[3016/1298] shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm transition-transform group-hover:shadow-md group-focus:shadow-md">
            <img
              src={currentFeature.image}
              alt={currentFeature.imageAlt}
              className="h-full w-full rounded-none object-contain transition-transform"
            />
          </div>

          <div className="min-h-[5.5rem] flex-1 space-y-2 px-2">
            <h4 className="text-base font-medium text-gray-900 transition-colors group-hover:text-mainblue">
              {currentFeature.headline}
            </h4>
            <p className="line-clamp-3 text-sm leading-relaxed text-gray-600">
              {currentFeature.description}
            </p>
          </div>
        </button>
      </div>

      <GenericModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={currentFeature.title}
        className="w-full max-w-2xl"
      >
        <div className="space-y-4">
          <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
            <img
              src={currentFeature.image}
              alt={currentFeature.imageAlt}
              className="w-full rounded-none object-contain"
            />
          </div>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              <p className="text-base font-semibold text-gray-900">
                {currentFeature.headline}
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-0.5 text-xs font-semibold tracking-wide text-blue-700 ring-1 ring-inset ring-blue-200/80">
                {currentFeature.monthYear}
              </span>
            </div>
            <p>{currentFeature.description}</p>
            <div className="space-y-3 text-[15px] leading-relaxed text-gray-700">
              {currentFeature.details?.map((detail, index) => (
                <p key={`${currentFeature.id}-detail-${index}`}>{detail}</p>
              ))}
            </div>
          </div>
        </div>
      </GenericModal>
    </RSCard>
  );
};

export default ProductFocus;
